<script setup>
import { ref, onMounted, watch } from 'vue'
import { useUserStore } from '@store/user'
import CommentsApi from '@/api/comments.api'
import MetaCommentCard from '@/views/projects/ProjectMeta/components/MetaCommentCard.vue'
import CommentForm from '@/components/CommentForm.vue'
import NoResultCommentsImg from '@/assets/img/no_result_comments.png'
import FeedbackFillIcon from '@/assets/svg/feedback-fill-Icon.svg?component'
import { ElNotification } from "element-plus";

const props = defineProps({
  type: { type: String, required: true }, // 'project' or 'document'
  id: { type: [Number, String], required: true },
  title: { type: String, default: 'Discussion & Feedback' },
  icon: { type: String, default: '@/assets/img/document-text.png' },
})

const emit = defineEmits(['commentsText'])

const userStore = useUserStore()
const comments = ref([])
const commentsText = ref('')

async function loadComments() {
  try {
    const res = await CommentsApi.list(props.type, props.id)
    const list = Array.isArray(res?.data?.comments)
      ? res.data.comments
      : Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res)
          ? res
          : []

    commentsText.value =
      list.filter((c) => c.is_resolved == 0).length +
      ' UnResolved of ' +
      list.length
    emit('commentsText', commentsText.value)

    comments.value = list
      .map((c) => ({
        id: c.id,
        page_number: c.page_number || 1,
        context: c.context,
        mentions: Array.isArray(c.mentions)
          ? c.mentions.map((m) => m?.name).filter(Boolean)
          : [],
        created_at: c.created_at,
        is_resolved: !!c.is_resolved,
        user: {
          name: c.user?.name,
          avatar: {
            url: c.user?.user_avatar?.url || c.user?.avatar?.url || null,
          },
        },
        replies: (Array.isArray(c.replies) ? c.replies : []).map((r) => ({
          id: r.id,
          reply: r.reply,
          text: r.reply,
          ts: r.created_at,
          created_at: r.created_at,
          user: {
            name: r.user?.name,
            avatar: { url: r.user?.user_avatar?.url || null },
          },
        })),
      }))
      .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
  } catch (error) {
    console.error('Error loading comments:', error)
  }
}

onMounted(loadComments)

// Watch for changes in comments and update commentsText
watch(
  comments,
  (newComments) => {
    const unresolvedCount = newComments.filter((c) => !c.is_resolved).length
    commentsText.value =
      unresolvedCount + ' unresolved of ' + newComments.length
    emit('commentsText', commentsText.value)
  },
  { deep: true }
)

function handleReply({ text, comment, done }) {
  const target = comments.value.find((c) => c.id === comment.id)
  if (!target) return

  CommentsApi.addReply(props.type, props.id, {
    comment_id: comment.id,
    reply: text,
  })
    .then((res) => {
      const fallbackUser = {
        name: userStore?.name || userStore?.user?.name || 'You',
      }
      const created = res?.data ||
        res || {
        id: Date.now(),
        reply: text,
        created_at: new Date().toISOString(),
        user: fallbackUser,
      }
      const reply = {
        id: created.id,
        reply: created.reply,
        text: created.reply,
        ts: created.created_at,
        created_at: created.created_at,
        user: {
          name: created.user?.name || fallbackUser.name,
          avatar: {
            url:
              created.user?.user_avatar?.url ||
              created.user?.userAvatar?.url ||
              null,
          },
        },
      }
      target.replies = [reply, ...(target.replies || [])]
    })
    .catch((error) => {
      console.error('Error adding reply:', error)
    })
    .finally(() => {
      if (typeof done === 'function') done()
    })
}

function handleResolve({ comment }) {
  const target = comments.value.find((c) => c.id === comment.id)
  if (!target) return
  // Optimistically mark as resolved
  target.is_resolved = true
  CommentsApi.resolve(props.type, props.id, {
    comment_id: comment.id,
  }).catch(() => {
    // Revert on failure
    target.is_resolved = false
  })
}

function handleCommentAdded(newComment) {
  comments.value = [newComment, ...comments.value]
}

// Allow parent to prepend a newly created comment
function prependComment(created) {
  if (!created) return
  const c = {
    id: created.id,
    page_number: created.page_number || 1,
    context: created.context,
    mentions: Array.isArray(created.mentions)
      ? created.mentions.map((m) => m?.name).filter(Boolean)
      : [],
    created_at: created.created_at,
    is_resolved: !!created.is_resolved,
    user: {
      name: created.user?.name,
      avatar: {
        url:
          created.user?.user_avatar?.url || created.user?.avatar?.url || null,
      },
    },
    replies: (Array.isArray(created.replies) ? created.replies : []).map(
      (r) => ({
        id: r.id,
        reply: r.reply,
        text: r.reply,
        ts: r.created_at,
        created_at: r.created_at,
        user: {
          name: r.user?.name,
          avatar: { url: r.user?.user_avatar?.url || null },
        },
      })
    ),
  }
  comments.value = [c, ...comments.value]
}

function handleDeleteComment(data) {
 
  const { type, id } = data;
  comments.value = comments.value.filter((c) => c.id !== id)
  CommentsApi.delete(type, id)
  .then((res) => {
    loadComments()
    })
}

defineExpose({ prependComment, reload: loadComments })
</script>

<template>
  <div class="card">
    <div class="card-body">
      <div class="d-flex align-items-center header">
        <FeedbackFillIcon />
        <div class="text-xs font-weight-bold profileName title-meta">{{ title }}</div>
      </div>
      <div class="row m-0 comments-section">
        <div class="col-12" v-if="comments.length > 0" v-for="c in comments" :key="c.id">
          <MetaCommentCard :comment="c" :replies="c.replies" :is-closed="!!c.is_resolved" @reply="handleReply"
            @goto="() => { }" @resolve="handleResolve" @deleteComment="handleDeleteComment" />
        </div>

        <div v-else class="text-center ">
          <div class="d-flex flex-column align-items-center justify-content-center" style="min-height: 260px;">
            <img :src="NoResultCommentsImg" alt="No Documents" style="width: 216px; height: 102px; opacity: 0.9;" />
            <div class=" hard-text">
              No Comments Yet
            </div>
            <div class="text-muted mb-3">
              Be the first to add your thoughts.
            </div>
          </div>
        </div>
        <CommentForm :type="type" :id="id" @comment-added="handleCommentAdded" />
      </div>


    </div>
  </div>
</template>

<style scoped>
img {
  width: 20px;
  margin-right: 10px;
}
</style>
